// One upgrade row, styled after the in-game workshop rows: name + detail on the
// left; − / editable level / + / MAX on the right. The +/- step size comes from
// the global ×1/×10/×100 toggle (like the game's buy multiplier); the level
// field itself accepts typed values.
import { useEffect, useRef, useState } from "react";
import { useStep } from "./StepContext";

interface Props {
  name: string;
  detail?: string;
  level: number;
  maxLevel: number;
  onChange: (level: number) => void;
}

export function LevelRow({ name, detail, level, maxLevel, onChange }: Props) {
  const { step } = useStep();
  const holdTimer = useRef<ReturnType<typeof setInterval>>();
  const levelRef = useRef(level);
  levelRef.current = level;
  const stepRef = useRef(step);
  stepRef.current = step;

  const [text, setText] = useState(String(level));
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setText(String(level));
  }, [level]);

  const clamp = (v: number) => Math.max(0, Math.min(maxLevel, Math.round(v)));

  const commitText = () => {
    editing.current = false;
    const parsed = parseInt(text, 10);
    if (Number.isNaN(parsed)) {
      setText(String(level));
    } else {
      const next = clamp(parsed);
      setText(String(next));
      onChange(next);
    }
  };

  const startHold = (sign: 1 | -1) => {
    onChange(clamp(levelRef.current + sign * stepRef.current));
    holdTimer.current = setInterval(() => {
      onChange(clamp(levelRef.current + sign * stepRef.current));
    }, 120);
  };
  const stopHold = () => clearInterval(holdTimer.current);

  const maxed = level >= maxLevel;

  return (
    <div className={`level-row${maxed ? " maxed" : ""}`}>
      <div className="level-row-info">
        <span className="level-row-name">{name}</span>
        {detail && <span className="level-row-detail">{detail}</span>}
      </div>
      <div className="level-row-controls">
        <button
          aria-label={`decrease ${name} by ${step}`}
          disabled={level <= 0}
          onPointerDown={() => startHold(-1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
        >
          −
        </button>
        <span className="level-row-level">
          <input
            type="text"
            inputMode="numeric"
            value={text}
            onFocus={(e) => {
              editing.current = true;
              e.target.select();
            }}
            onChange={(e) => setText(e.target.value)}
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            aria-label={`${name} level`}
          />
          <small>/{maxLevel}</small>
        </span>
        <button
          aria-label={`increase ${name} by ${step}`}
          disabled={maxed}
          onPointerDown={() => startHold(1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
        >
          +
        </button>
        <button className="level-row-max" disabled={maxed} onClick={() => onChange(maxLevel)}>
          MAX
        </button>
      </div>
    </div>
  );
}
